import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, BookOpen, Eye, EyeOff, X, Tag } from 'lucide-react';
import { formatDate } from '../../lib/utils';

const KB_CATEGORIES = [
  'Getting Started',
  'Billing & Payments',
  'WordPress',
  'WooCommerce',
  'Hosting',
  'Email',
  'Google Ads',
  'Meta Ads',
  'Troubleshooting',
  'General',
];

const emptyArticle = {
  title: '',
  content: '',
  keywords: [],
  category: 'General',
  is_published: true,
};

export default function AdminKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showPublishedOnly, setShowPublishedOnly] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState(emptyArticle);
  const [newKeyword, setNewKeyword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await adminAPI.getAllArticles();
      setArticles(response.data);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.keywords?.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = !filterCategory || article.category === filterCategory;
    const matchesPublished = !showPublishedOnly || article.is_published;
    return matchesSearch && matchesCategory && matchesPublished;
  });

  const handleCreate = () => {
    setEditingArticle(null);
    setFormData({ ...emptyArticle, keywords: [] });
    setShowDialog(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      keywords: article.keywords || [],
      category: article.category,
      is_published: article.is_published,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Article title is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Article content is required');
      return;
    }

    setSaving(true);
    try {
      if (editingArticle) {
        await adminAPI.updateArticle(editingArticle.id, formData);
        toast.success('Article updated successfully');
      } else {
        await adminAPI.createArticle(formData);
        toast.success('Article created successfully');
      }
      setShowDialog(false);
      loadArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    try {
      await adminAPI.deleteArticle(articleId);
      toast.success('Article deleted');
      setShowDeleteConfirm(null);
      loadArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleTogglePublished = async (article) => {
    try {
      await adminAPI.updateArticle(article.id, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      loadArticles();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, newKeyword.trim()],
      });
      setNewKeyword('');
    }
  };

  const removeKeyword = (index) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter((_, i) => i !== index),
    });
  };

  const categoryCounts = articles.reduce((acc, article) => {
    acc[article.category] = (acc[article.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Knowledge Base Management</h1>
            <p className="text-muted-foreground">Create and manage help articles for your customers</p>
          </div>
          <Button onClick={handleCreate} data-testid="create-article-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Articles</p>
                  <p className="text-2xl font-bold">{articles.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Eye className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Published</p>
                  <p className="text-2xl font-bold">{articles.filter(a => a.is_published).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <EyeOff className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Drafts</p>
                  <p className="text-2xl font-bold">{articles.filter(a => !a.is_published).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Tag className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Categories</p>
                  <p className="text-2xl font-bold">{Object.keys(categoryCounts).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder="Search articles by title, content, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="article-search-input"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background"
                data-testid="article-category-filter"
              >
                <option value="">All Categories</option>
                {KB_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat} {categoryCounts[cat] ? `(${categoryCounts[cat]})` : ''}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <Switch
                  checked={showPublishedOnly}
                  onCheckedChange={setShowPublishedOnly}
                />
                <Label className="text-sm">Published only</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredArticles.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Articles Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || filterCategory ? 'Try adjusting your filters' : 'Get started by creating your first knowledge base article'}
              </p>
              {!searchQuery && !filterCategory && (
                <Button onClick={handleCreate}>Create Article</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredArticles.map((article) => (
              <Card key={article.id} className={!article.is_published ? 'opacity-70 border-dashed' : ''} data-testid={`article-card-${article.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={article.is_published ? 'default' : 'secondary'}>
                          {article.is_published ? 'Published' : 'Draft'}
                        </Badge>
                        <Badge variant="outline">{article.category}</Badge>
                      </div>
                      <CardTitle className="text-lg line-clamp-1">{article.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="line-clamp-2 mb-3">{article.content}</CardDescription>
                  
                  {/* Keywords */}
                  {article.keywords && article.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {article.keywords.slice(0, 5).map((keyword, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-muted">
                          {keyword}
                        </Badge>
                      ))}
                      {article.keywords.length > 5 && (
                        <Badge variant="outline" className="text-xs">+{article.keywords.length - 5}</Badge>
                      )}
                    </div>
                  )}

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>{article.views || 0} views</span>
                    <span>Updated {formatDate(article.updated_at)}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(article)} data-testid={`edit-article-${article.id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleTogglePublished(article)}
                    >
                      {article.is_published ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {article.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive ml-auto"
                      onClick={() => setShowDeleteConfirm(article.id)}
                      data-testid={`delete-article-${article.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Edit Article' : 'Create New Article'}</DialogTitle>
              <DialogDescription>
                {editingArticle ? 'Update article details' : 'Add a new article to your knowledge base'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., How to update WordPress plugins"
                  data-testid="article-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    data-testid="article-category-select"
                  >
                    {KB_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                  />
                  <Label>Publish immediately</Label>
                </div>
              </div>

              <div>
                <Label>Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your article content here. You can use basic formatting..."
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="article-content-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Tip: Use clear headings and bullet points for better readability</p>
              </div>

              {/* Keywords */}
              <div>
                <Label>Keywords (for search)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Add a keyword..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  />
                  <Button type="button" variant="outline" onClick={addKeyword}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.keywords.map((keyword, index) => (
                    <Badge key={index} variant="secondary" className="pr-1">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving} data-testid="save-article-btn">
                {saving ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Article</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this article? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => handleDelete(showDeleteConfirm)}>
                Delete Article
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
